'use strict';

// external

// shared imports
import Joi from '~/shared/src/utilities/joiExtended';
import Item, {
    ItemAttributes
} from '~/shared/src/ci/Item';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataStructure,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    ColumnDefinition,
    ColumnFilter,
    ColumnSorter,
    filtersSchema,
    FilterValueType,
    searchStringSchema,
    sortersSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {BaseRequestParameters} from '~/shared/src/model/BaseRequest';
import {Action} from '~/shared/src/utilities/action';
import {Column} from '~/shared/src/utilities/column';
import {Function} from '~/shared/src/utilities/function';
import {Message} from '~/shared/src/utilities/message';
import {Query} from '~/shared/src/utilities/query';
import {Style} from '~/shared/src/utilities/style';
import {isColumnEditable} from '~/shared/src/utilities/modules/datatable';
import dataUtilities from '~/shared/src/utilities/dataUtils';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {
    OverviewInitialGroupState,
    OverviewLinkTarget,
    OverviewRowsPerPage
} from '~/shared/src/utilities/overview';


/*** type definitions ************************************************************************************************/

export type ColumnKeyToFieldMapping = {
    [ columnKey : string ] : string
};
export type OpenDetailViewConfig = {
    queryFieldName : string;
    itemTypeNameOrQueryFieldName : string;
};


/*** config definitions ********************************************************************************************/


/*** method types ****************************************************************************************************/

/*** loadConfig ***/
export type LoadConfigParameters = Partial<BaseRequestParameters> & {
    viewKey : string;
};
export type LoadConfigResult = {
    success : boolean;
    data : {
        actions? : Action[];
        autoLayout : boolean;
        columnDefinitions : ColumnDefinition[];
        columnKeyToFieldMapping? : ColumnKeyToFieldMapping;
        configHash : string;
        dataStructure : DataStructure;
        defaultFilters : ColumnFilter[];
        defaultSorters : ColumnSorter[];
        deletable : boolean;
        editMode : boolean;
        editable : boolean;
        filterable : boolean;
        fixFilters : ColumnFilter[];
        forceFit : boolean;
        functions? : Function[];
        groupingColumn? : string;
        groupingHeader? : string | Message;
        iconCls : string;
        initialGroupState : OverviewInitialGroupState;
        linkTarget : OverviewLinkTarget;
        openDetailViewConfig? : OpenDetailViewConfig;
        panelConfigIds? : number[];
        queryType : Query[ 'type' ];
        reorderable : boolean;
        resizable : boolean;
        rowStyling : Style[];
        rowsPerPage : OverviewRowsPerPage;
        schema : Schema;
        sortable : boolean;
        tabTitle? : string | Message;
        title : string | Message;
        tooltipTitle? : string | Message;
        usePaging : boolean;
        viewName : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadConfigDataParameters = Partial<BaseRequestParameters> & ( {
    viewId : string;
    viewKey : string;
} );
export type LoadConfigDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
        rootItemId : number;
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};

export type  LoadColumnDataDescriptorsParameters = {
    schema : Schema;
    type : Exclude<Query['type'], 'DataStructure'>;
    query : string;
    parameters? : string[];
};
export type LoadColumnDataDescriptorsResult = {
    success : boolean;
    data : {
        columnDataDescriptors : StaticDataDescriptor[];
    };
    metaData : {
        metrics : Metric[];
    };
};

/*** clearConfig ***/
export type ClearConfigParameters = {
    configHash? : string;
};
export type ClearConfigResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    };
};


export type GetOverviewItemIdByDisplayAttributeParameters = {
    displayAttributeValue : string;
};
export type GetOverviewItemIdByDisplayAttributeResult = {
    success : true;
    data : {
        rootItemId? : number;
    };
    metaData : {
        metrics : Metric[];

        // debug
        getOverviewItemId? : {
            sql? : string;
            parameters? : {
                [ parameter : string ] : any
            };
        };
    };
};


export type GetViewKeyByNameParameters = {
    viewName : string;
};
export type GetViewKeyByNameResult = {
    success : true;
    data : {
        viewKey? : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

/*** loadData common***/
export type LoadDataCommonParameters = {
    searchString? : string;
    columns? : string[];
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
    parameters? : {
        [ parameter : string ] : string;
    };
};
export const loadDataCommonParameterDefaults = {
    columns: [],
    filters: [],
    sorters: [],
    start  : 0,
    limit  : 100
};

export const loadDataCommonParametersSchema = Joi.object( {
    searchString: searchStringSchema,
    columns     : Joi.array()
        .items(
            Joi.string()
        )
        .description( 'The fields the returned set should contain.' ),
    filters     : filtersSchema,
    sorters     : sortersSchema,
    start       : Joi.number()
        .default( 0 )
        .description( 'The offset of the full result set the returned set should start at.' ),
    limit       : Joi.number()
        .default( 100 )
        .description( 'The number of entries the returned set should contain.' ),
    parameters  : Joi.object()
        .pattern( /.*/, Joi.alternatives().try( Joi.string().allow( '' ), Joi.number(), Joi.boolean() ) )
        .description( 'The parameters for the query with their values.' )
} );

/*** loadData ***/
export type LoadDataParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    viewKey : string;
    configHash? : string;
};
export type LoadMetaDataRowData = { [ label : string ] : any };
export type LoadMetaDataRowDataMapping = { [ field : string ] : string };
export type LoadDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

/*** loadDataForDataStructure ***/
export type LoadDataForDataStructureParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    columnsToSelect : DataDescriptor[];
    dataStructure : DataStructure;
    searchColumns? : string[];
};
/*** loadDataForQuery ***/
export type LoadDataForQueryParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    columnsToSelect : DataDescriptor[];
    query : string;
    searchColumns? : string[];
    type : Exclude<Query[ 'type' ], 'DataStructure'>;
};


/*** deleteOverview ***/
export type DeleteOverviewParameters = {
    viewId : number;
    historizeOnly? : boolean;
};
export type DeleteOverviewResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    }
};


/**
 * Gets the columns for sql query by resolving result fields from database and the extracted columns
 *
 * @param {( StaticDataDescriptor | ExpressionDataDescriptor )[]} columnDataDescriptors
 * @param {Item} item
 * @param {string} dataContext
 * @param {boolean} [useColumnDefinitionOnly=false]
 *
 * @return {Column[]}
 *
 */
export const getColumnsForSqlQuery = ( columnDataDescriptors : StaticDataDescriptor[], item : Item, dataContext : string, useColumnDefinitionOnly : boolean = false, createDataDescriptor : boolean = false ) : Column[] => {
    const retVal : Column[] = [],
          columns           = dataExtraction.extractColumns( item, dataContext, getDataStructureForSqlQuery() );

    // get columns for dataDescriptors
    for ( const columnDataDescriptor of columnDataDescriptors ) {
        let column = columns.find( col => col.data.queryFieldName === columnDataDescriptor.alias );

        // get column from definition
        if ( column ) {
            column.data.valueType = columnDataDescriptor.valueType ?? 'STRING';
        }

        // get column for columnDataDescriptor
        else if ( !useColumnDefinitionOnly ) {
            let columnDataKey = columnDataDescriptor.alias,
                inc           = 1;

            while ( retVal.find( c => c.data.key === columnDataKey ) ) {
                columnDataKey = `${columnDataDescriptor.alias}:${inc++}`;
            }

            column = {
                data: {
                    id        : Item.getUniqueId(),
                    key       : columnDataKey,
                    name      : columnDataKey,
                    isEditable: false,
                    isHidden  : !!columns.length,
                    valueType : columnDataDescriptor.valueType ?? 'STRING',
                    width     : 100
                }
            };
        }

        if ( column ) {
            column.dataDescriptor = columnDataDescriptor;

            retVal.push( column );
        }
    }

    // get columns for columns
    if ( createDataDescriptor && retVal.length !== columns.length ) {
        const dataStructure = getDataStructureForSqlQuery();

        for ( const column of columns ) {
            if ( !retVal.find( r => r.dataDescriptor!.alias === column.data.queryFieldName ) ) {
                const dataDescriptor = buildDataDescriptor( { queryFieldName: column.data.queryFieldName ?? 'createdQueryFieldName' }, dataStructure );

                if ( dataDescriptor ) {
                    dataDescriptor.fullAlias = `createdDataDescriptor::${dataDescriptor.fullAlias}`;
                }

                if ( dataDescriptor ) {
                    column.dataDescriptor = dataDescriptor;

                    retVal.push( column );
                }
            }
        }
    }

    // order columns
    retVal.sort( ( column1, column2 ) => {
        let sortRetVal = 0;

        if ( column1.data.order && column2.data.order ) {
            if ( column1.data.order > column2.data.order ) {
                sortRetVal = 1;
            }
            else if ( column1.data.order < column2.data.order ) {
                sortRetVal = -1;
            }
        }

        return sortRetVal;
    } );

    return retVal;
};

/**
 * Gets the columnDefinitions.
 *
 * @param {Column[]} columns
 * @param {DataStructure} dataStructure
 * @param {Schema} schema
 *
 * @return {ColumnDefinition[]}
 */
export const getColumnDefinitions = ( columns : Column[], dataStructure : DataStructure, schema? : Schema ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [];

    for ( const column of columns ) {

        // skip column if user has no read permission
        if ( column.permissions?.read?.length && !global.user?.isMemberOfGroup( column.permissions.read ) ) {
            continue;
        }

        // skip column if there is no dataDescriptor for
        if ( !column.dataDescriptor ) {
            continue;
        }

        const columnData     = column.data,
              columnMessages = column.messages;

        let header = columnMessages?.title;

        if ( !header && schema ) {
            header = dataUtils.getMetaModelLabelMsgId( column.dataDescriptor, dataStructure, schema );
        }

        if ( !header ) {
            header = columnData.name;
        }

        retVal.push( {
            // key
            key: column.data.key,

            // dataDescriptor
            field: column.dataDescriptor.alias,

            // DefinitionDescriptor
            header        : header,
            headerStyling : column.styles?.headerStyle,
            footer        : columnMessages?.footer,
            dataDescriptor: column.dataDescriptor,
            permissions   : column.permissions,

            editor                   : isColumnEditable( column.dataDescriptor, dataStructure, global.schema, column.permissions ) && ( columnData.isEditable ?? true ),
            editorConfig             : columnData.editorConfig,
            editorDefaultValue       : columnData.defaultValue,
            editorHelpText           : columnMessages?.helpText,
            editorValidatorRegExp    : columnData.validator,
            editorValidationErrorText: columnMessages?.validatorErrorText,

            filter      : columnData.filterConfig === false ? false : true,
            filterConfig: columnData.filterConfig,

            tooltip: columnMessages?.tooltip,

            valueType: columnData.valueType,

            panelConfigIds: column.panelConfigIds,

            // ConfigDescriptor
            align : columnData.alignment,
            format: column.format && Object.keys( column.format ).length ? column.format : undefined,
            // frozen
            hidable: columnData.isHidable ?? true,
            hidden : columnData.isHidden ?? false,
            // reorderable
            // resizable
            searchColumn: columnData.isSearchColumn ?? false,
            // sortable
            styling : column.styles?.style,
            width   : columnData.width,
            maxWidth: columnData.widthMax,
            minWidth: columnData.widthMin
        } as ColumnDefinition );
    }

    return retVal;
};

/**
 * Gets the fix filters.
 *
 * @param {Column[]} columns
 * @param {DataStructure} dataStructure
 *
 * @return {ColumnFilter[]}
 */
export const getFixFilters = ( columns : Column[], dataStructure : DataStructure ) : ColumnFilter[] => {
    const retVal : ColumnFilter[] = [];

    for ( const column of columns ) {
        const columnData = column.data;

        if ( column.dataDescriptor && columnData.filterOperator && columnData.filterIsFixed ) {
            if ( columnData.filterOperator === 'empty' || columnData.filterOperator === 'notEmpty' ) {
                retVal.push( {
                    key     : column.data.key,
                    field   : column.dataDescriptor.alias,
                    operator: columnData.filterOperator
                } );
            }
            else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                retVal.push( {
                    key      : column.data.key,
                    field    : column.dataDescriptor.alias,
                    operator : columnData.filterOperator,
                    value    : columnData.filterValue,
                    valueType: dataUtilities.getDataValueType( column.dataDescriptor, global.schema, dataStructure ) as FilterValueType
                } );
            }
        }
    }

    return retVal
};

/**
 * Gets the default filters.
 *
 * @param {Column[]} columns
 * @param {DataStructure} dataStructure
 *
 * @return {ColumnFilter[]}
 */
export const getDefaultFilters = ( columns : Column[], dataStructure : DataStructure ) : ColumnFilter[] => {
    const retVal : ColumnFilter[] = [];

    for ( const column of columns ) {
        const columnData = column.data;

        if ( column.dataDescriptor && columnData.filterOperator && !columnData.filterIsFixed ) {
            if ( columnData.filterOperator === 'empty' || columnData.filterOperator === 'notEmpty' ) {
                retVal.push( {
                    key     : column.data.key,
                    field   : column.dataDescriptor.alias,
                    operator: columnData.filterOperator
                } );
            }
            else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                retVal.push( {
                    key      : column.data.key,
                    field    : column.dataDescriptor.alias,
                    operator : columnData.filterOperator,
                    value    : columnData.filterValue,
                    valueType: dataUtilities.getDataValueType( column.dataDescriptor, global.schema, dataStructure ) as FilterValueType
                } );
            }
        }
    }

    return retVal;
};

/**
 * Gets the default sorters.
 *
 * @param {Column[]} columns
 *
 * @return {ColumnSorter[]}
 */
export const getDefaultSorters = ( columns : Column[] ) : ColumnSorter[] => {
    let retVal : ColumnSorter[] = [];

    for ( const column of columns ) {
        const columnData = column.data;

        if ( column.dataDescriptor && columnData.sortOrder ) {
            retVal[ Number( columnData.sortOrder ) - 1 ] = {
                key      : column.data.key,
                field    : column.dataDescriptor.alias,
                direction: columnData?.sortDirection ?? 'ASC' as SortDirection
            };
        }
    }

    retVal = retVal.reduce(
        ( reducedColumnSorters, columnSorter ) => {
            if ( columnSorter ) {
                reducedColumnSorters.push( columnSorter );
            }

            return reducedColumnSorters;
        },
        [] as ColumnSorter[]
    );

    return retVal;
};
